import { useState } from 'react';
import { faqs as Api, type Faq, type FaqInput, type PubState } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row, StateTag } from '../components/Field';

/**
 * The questions on the home page.
 *
 * A screen of their own rather than a box of text on the Landing screen: the
 * desk adds to these after support conversations, and a question is a thing
 * with a state — an answer can be written before it is shown.
 */
export function Faqs() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Faq | 'new' | null>(null);
  const rows = data?.items ?? [];
  const sel = useSelection(rows);
  const live = rows.filter((f) => f.state === 'published').length;

  async function move(row: Faq, by: number) {
    const ordered = [...rows];
    const i = ordered.findIndex((f) => f.id === row.id);
    const to = i + by;
    if (i === -1 || to < 0 || to >= ordered.length) return;
    const [moved] = ordered.splice(i, 1);
    if (!moved) return;
    ordered.splice(to, 0, moved);
    /* Every row is renumbered, not just the two that swapped: an order is a
       statement about all of them, and half of one leaves the rest sorted by
       whatever they happened to hold. */
    await Promise.all(ordered.map((f, n) => (f.sort === n + 1 ? null : Api.update(f.id, {
      question: f.question, answer: f.answer, state: f.state, sort: n + 1,
    }))).filter(Boolean));
    await reload();
  }

  async function remove(row: Faq) {
    if (!confirm(`Delete “${row.question}”?`)) return;
    await Api.remove(row.id);
    await reload();
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>FAQ</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={230} h={12} style={{ marginTop: 3 }} />
              : `${live} on the home page, ${rows.length} in all`}
            {' · '}the heading above them is on the Landing screen
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New question</button>
      </div>

      {error ? <div className="err">{error.message}</div> : null}

      <BulkActions
        path="/faqs" noun="question"
        rows={sel.picked.map((f) => ({ id: f.id, label: String(f.question) }))}
        actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
        onDone={async () => { sel.clear(); await reload(); }}
      />

      <div className="crd" style={{ padding: 0 }}>
        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th>Question</th><th>State</th><th>Order</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="muted" style={{ padding: '26px 8px' }}>
                  No questions yet. The band leaves the home page until there is one.
                </td></tr>
              ) : rows.map((f, i) => (
                <tr key={f.id} style={sel.isOn(f.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(f.id)} label={String(f.question)}
                    onChange={(v) => sel.toggle(f.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700 }}>{f.question}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)', maxWidth: 560, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.answer}</div>
                  </td>
                  <td><StateTag state={f.state} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" type="button" aria-label={`Move ${f.question} up`}
                      style={{ padding: '3px 9px', fontSize: 12 }}
                      disabled={i === 0} onClick={() => void move(f, -1)}>↑</button>
                    <button className="b bo" type="button" aria-label={`Move ${f.question} down`}
                      style={{ padding: '3px 9px', fontSize: 12, marginLeft: 5 }}
                      disabled={i === rows.length - 1} onClick={() => void move(f, 1)}>↓</button>
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(f)}>Edit</button>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => void remove(f)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <FaqForm
          faq={editing === 'new' ? null : editing}
          nextSort={rows.length + 1}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function FaqForm({ faq, nextSort, onCancel, onSaved }: {
  faq: Faq | null;
  nextSort: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<FaqInput>(() => faq
    ? { question: faq.question, answer: faq.answer, sort: faq.sort, state: faq.state }
    : { question: '', answer: '', sort: nextSort, state: 'draft' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const set = <K extends keyof FaqInput>(k: K, v: FaqInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.question.trim()) { setError('A question is required.'); return; }
    if (!f.answer.trim()) { setError('An answer is required — a question that opens onto nothing is worse than none.'); return; }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      if (faq) await Api.update(faq.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={faq ? 'Edit question' : 'New question'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {faq?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}
      <Field label="Question">
        <input className="inp" value={f.question} autoFocus
          onChange={(e) => set('question', e.target.value)} />
      </Field>
      <Field label="Answer" hint="Plain words. It is read on the home page, by someone deciding whether to buy.">
        <textarea className="inp ta" rows={5} value={f.answer}
          onChange={(e) => set('answer', e.target.value)} />
      </Field>
      <Row cols="120px">
        <Field label="Order"><input className="inp" type="number" value={f.sort}
          onChange={(e) => set('sort', Number(e.target.value))} /></Field>
      </Row>
    </Modal>
  );
}
